import {
	IconClockPause,
	IconDots,
	IconPlayerPlay,
	IconPlayerPlayFilled,
	IconRefresh,
	IconTrash,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Modal } from "antd";
import { useEffect, useRef } from "react";
import {
	useDeleteJob,
	useJob,
	useJobRuns,
	usePauseJob,
	useResumeJob,
	useTriggerJobRun,
	useUpdateJob,
} from "@/common/artemis-operations/jobs";
import { JobStatus } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import {
	BaseContainer,
	BasePageContainer,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import { useFrictionConfirm } from "@/components/friction-confirm-dialog";
import { JobDetails } from "@/components/jobs/job-details";
import { JobRunsTable } from "@/components/jobs/job-runs-table";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/jobs/$jobId",
)({ component: JobPage });

function JobPage() {
	const { orgSlug, jobId, workspaceId } = Route.useParams();
	const navigate = useNavigate();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();

	const { data: job, isPending, isError, refetch } = useJob(jobId);
	const notify = useNotify();
	const notifyRef = useRef(notify);
	notifyRef.current = notify;

	const { mutate: pause, isPending: isPausing } = usePauseJob();
	const { mutate: resume, isPending: isResuming } = useResumeJob();
	const { mutate: deleteJob, isPending: isDeleting } = useDeleteJob();
	const { mutate: trigger, isPending: isTriggering } = useTriggerJobRun();
	const { mutate: updateJob } = useUpdateJob(jobId);
	const { confirm, dialog } = useFrictionConfirm();

	const runsList = useJobRuns(jobId);

	useEffect(() => {
		setTitle(job?.name ?? "Scheduled Job");
	}, [setTitle, job]);

	useEffect(() => {
		if (!job) return;

		const isActive = job.status === JobStatus.ACTIVE;
		const isPaused = job.status === JobStatus.PAUSED;

		setActions([
			{
				type: "button",
				id: "trigger",
				disabled: isTriggering,
				tooltip: "Trigger job",
				icon: <CustomIcon icon={IconPlayerPlayFilled} />,
				onClick: () =>
					trigger(job.id as string, {
						onSuccess: () => {
							notifyRef.current.success(
								"Run triggered",
								"A new run has been queued.",
							);
							refetch();
						},
						onError: () =>
							notifyRef.current.error(
								"Failed to trigger run",
								"The run could not be started. Try again.",
							),
					}),
			},
			...(isActive
				? [
						{
							type: "button" as const,
							id: "pause",
							tooltip: "Pause job",
							disabled: isPausing,
							icon: <CustomIcon icon={IconClockPause} />,
							onClick: () =>
								Modal.confirm({
									title: "Pause this job?",
									content:
										"The job's schedule will be suspended and no new runs will be created until you resume it. In-flight runs are not affected. You can resume the job at any time.",
									okText: "Pause",
									cancelText: "Cancel",
									onOk: () =>
										pause(job.id as string, {
											onSuccess: () => {
												notifyRef.current.success(
													"Job paused",
													"No new runs will be fired until the job is resumed.",
												);
												refetch();
											},
											onError: () =>
												notifyRef.current.error(
													"Failed to pause job",
													"Please try again.",
												),
										}),
								}),
						},
					]
				: []),
			...(isPaused
				? [
						{
							type: "button" as const,
							id: "resume",
							tooltip: "Resume job",
							disabled: isResuming,
							icon: <CustomIcon icon={IconPlayerPlay} />,
							onClick: () =>
								Modal.confirm({
									title: "Resume this job?",
									content:
										"The job's schedule will be re-enabled and it will start firing new runs again at its next scheduled time.",
									okText: "Resume",
									cancelText: "Cancel",
									onOk: () =>
										resume(job.id as string, {
											onSuccess: () => {
												notifyRef.current.success(
													"Job resumed",
													"The job will fire at its next scheduled time.",
												);
												refetch();
											},
											onError: () =>
												notifyRef.current.error(
													"Failed to resume job",
													"Please try again.",
												),
										}),
								}),
						},
					]
				: []),
			{
				type: "submenu",
				id: "settings",
				icon: <CustomIcon icon={IconDots} />,
				submenu: [
					{
						type: "button" as const,
						id: "delete",
						label: "Delete",
						danger: true,
						tooltip: "Delete job",
						disabled: isDeleting,
						icon: <CustomIcon icon={IconTrash} />,
						onClick: () =>
							confirm({
								title: "Delete this job?",
								message:
									"This permanently deletes the job, its schedule, and its entire run history. This cannot be undone.",
								onConfirm: () =>
									deleteJob(jobId, {
										onSuccess: () => {
											notifyRef.current.success(
												"Job deleted",
												"The job and its run history have been removed.",
											);
											navigate({
												to: "/$orgSlug/workspaces/$workspaceId/jobs",
												params: { orgSlug, workspaceId },
											});
										},
										onError: () =>
											notifyRef.current.error(
												"Failed to delete job",
												"Please try again.",
											),
									}),
							}),
					},
					{
						type: "button",
						id: "refresh",
						label: "Refresh",
						tooltip: "Refresh",
						icon: <CustomIcon icon={IconRefresh} />,
						onClick: () => refetch(),
					},
				],
			},
		]);
		return () => setActions([]);
	}, [
		setActions,
		refetch,
		job,
		orgSlug,
		isPausing,
		isResuming,
		isDeleting,
		isTriggering,
		pause,
		resume,
		deleteJob,
		navigate,
		workspaceId,
		trigger,
		jobId,
		confirm,
	]);

	return (
		<QueryGuard isPending={isPending} isError={isError} isNull={!job}>
			<BasePageContainer>
				<BaseContainer title="Details">
					<JobDetails
						job={job!}
						onSaveName={(name) =>
							updateJob(
								{ name },
								{
									onSuccess: () => notify.success("Saved", "Job name updated."),
									onError: () =>
										notify.error("Failed to save", "Could not update the job."),
								},
							)
						}
						onSaveDescription={(description) =>
							updateJob(
								{ description },
								{
									onSuccess: () =>
										notify.success("Saved", "Description updated."),
									onError: () =>
										notify.error("Failed to save", "Could not update the job."),
								},
							)
						}
						onSaveInstructions={(instructions) =>
							updateJob(
								{ instructions },
								{
									onSuccess: () =>
										notify.success("Saved", "Instructions updated."),
									onError: () =>
										notify.error("Failed to save", "Could not update the job."),
								},
							)
						}
						onSaveAgent={(agentId) =>
							updateJob(
								{ agentId },
								{
									onSuccess: () => notify.success("Saved", "Agent updated."),
									onError: () =>
										notify.error("Failed to save", "Could not update the job."),
								},
							)
						}
						onSaveSchedule={(schedule) =>
							updateJob(
								{ schedule },
								{
									onSuccess: () => notify.success("Saved", "Schedule updated."),
									onError: () =>
										notify.error("Failed to save", "Could not update the job."),
								},
							)
						}
					/>
				</BaseContainer>

				<BaseContainer title="Runs">
					<JobRunsTable
						data={runsList.rows}
						loading={runsList.isFetching}
						totalCount={runsList.totalCount}
						hasNextPage={runsList.hasNextPage}
						hasPreviousPage={runsList.hasPreviousPage}
						pageIndex={runsList.pageIndex}
						onNextPage={runsList.onNextPage}
						onPreviousPage={runsList.onPreviousPage}
						pageSize={50}
						manualSorting
						initialSorting={runsList.sort}
						onSortingChange={runsList.setSort}
					/>
				</BaseContainer>
				{dialog}
			</BasePageContainer>
		</QueryGuard>
	);
}
